import React, { memo, useMemo } from "react";
import { TExam } from "../../shared/types/exam.type";
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  formatDate,
  getCorrectDateFormat,
  sortGroupedDates,
} from "../../shared/utils/DateUtils";
import { Ttraining } from "../../shared/types/training.type";
import { Icon } from "@iconify/react";
import { t } from "i18next";
import { get, groupBy, isEmpty, toArray, upperCase } from "lodash";
import { useAuth } from "../../hooks/useAuth";
import { routes } from "../../pages/routes";
import { EditNote } from "@mui/icons-material";
import { setEditTrainingsModal } from "../../store/reducers/modal.reducer";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { useExam } from "../../hooks/useExam";
import { useUserActivityLog } from "../../hooks/useUserActivityLog";

type ListExamProps = {
  exams: Ttraining[];
  isLoading: boolean;
  displayWaiting: boolean;
  loadMore: () => void;
  onDisplayChange: (value: boolean) => void;
  allLoaded: boolean;
};

function ListExam({
  exams,
  isLoading,
  loadMore,
  allLoaded,
  displayWaiting,
  onDisplayChange,
}: ListExamProps) {
  const { auth } = useAuth();
  const dispatch = useDispatch();
  const { getEqmTitle } = useExam(false);
  const { track } = useUserActivityLog();

  const groupedExams = useMemo(() => {
    if (isEmpty(exams)) return [];
    let userDateFormat = auth?.default_date_format
      ? getCorrectDateFormat(auth?.default_date_format)
      : "DD/MM/YYYY";

    let groupedExams = groupBy(exams, (exam) =>
      formatDate(exam?.creation_date, userDateFormat),
    );

    return sortGroupedDates(groupedExams, userDateFormat, "DESC");
  }, [exams, auth?.default_date_format]);

  //Fns
  const openEditTrainingsModal = () => {
    dispatch(setEditTrainingsModal(true));
  };

  const renderExamInfo = (exam: Ttraining) => {
    return (
      <Box
        display={"flex"}
        flexDirection={{ md: "row", xs: "column" }}
        gap={{ md: 1, xs: 0.5 }}
        alignItems={{ md: "center", xs: "self-start" }}
      >
        <Tooltip arrow title={t("dateTimeTime")}>
          <Typography
            color={"white"}
            variant="body1"
            display={"flex"}
            alignItems={"center"}
            gap={1}
          >
            <Icon
              style={{ width: 17, height: 17 }}
              icon="mingcute:time-line"
            ></Icon>{" "}
            {formatDate(exam.creation_date, "LT")}
          </Typography>
        </Tooltip>
        {(exam.training_type?.display_name ||
          exam?.training_type_automatic?.display_name) && (
          <>
            <Divider flexItem orientation="vertical"></Divider>
            <Tooltip arrow title={t("editTrainingForm:trainingType:label")}>
              <Typography
                color={"white"}
                variant="body1"
                display={"flex"}
                alignItems={"center"}
                gap={1}
              >
                <Icon
                  style={{ width: 17, height: 17 }}
                  icon="maki:horse-riding"
                ></Icon>{" "}
                {exam.training_type?.display_name ??
                  exam?.training_type_automatic?.display_name ??
                  " - "}
              </Typography>
            </Tooltip>
          </>
        )}
        {(exam.track_name?.display_name ||
          exam.track_name_automatic?.display_name) && (
          <>
            <Divider flexItem orientation="vertical"></Divider>
            <Tooltip arrow title={t("editTrainingForm:trackName:label")}>
              <Typography
                color={"white"}
                variant="body1"
                display={"flex"}
                alignItems={"center"}
                gap={1}
              >
                <Icon style={{ width: 17, height: 17 }} icon="bx:map"></Icon>{" "}
                {exam.track_name?.display_name ??
                  exam.track_name_automatic?.display_name ??
                  "-"}
              </Typography>
            </Tooltip>
          </>
        )}
        {(!exam.horse_trainer?.horse ||
          exam.horse_trainer?.horse?.needs_to_be_named) && (
          <>
            <Divider flexItem orientation="vertical"></Divider>
            <Tooltip arrow title={t("training:device")}>
              <Typography
                color={"white"}
                variant="body1"
                display={"flex"}
                alignItems={"center"}
                gap={1}
              >
                <Icon style={{ width: 17, height: 17 }} icon="bx:chip"></Icon>{" "}
                {getEqmTitle(exam.equimetre)}
              </Typography>
            </Tooltip>
          </>
        )}
      </Box>
    );
  };

  return (
    <Stack direction={"column"} spacing={1}>
      <Box display={"flex"} flexWrap={"wrap"} justifyContent={"end"} gap={1}>
        <Button
          onClick={() => track("COMPARE_TODAY_TRAININGS_BUTTON_CLICKED")}
          disabled={isLoading}
          component={Link}
          disableRipple
          to={routes.DASHBOARD_ROUTES.COMPARAISON}
          startIcon={<Icon icon="carbon:compare"></Icon>}
          variant="outlined"
        >
          {t("training:compareLatestTrainings")}
        </Button>
        <Button
          disabled={isLoading}
          onClick={openEditTrainingsModal}
          startIcon={<EditNote></EditNote>}
          variant="outlined"
        >
          {t("editTrainingForm:fulfillConditionsOfTodaysTrainings")}
        </Button>
      </Box>
      <Card sx={{ maxHeight: 500, position: "relative", overflowY: "auto" }}>
        {/* Scrollable container */}
        <Box
          sx={{ maxHeight: "100%", overflowY: "auto", position: "relative" }}
        >
          <CardContent>
            <Box
              mb={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Typography variant="h5">{t("myTrainings")}</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={displayWaiting}
                    onChange={(_, checked) => onDisplayChange(checked)}
                  />
                }
                label={t("button:showUnassigned")}
              />
            </Box>

            <List disablePadding>
              {groupedExams.map((group, index) => (
                <React.Fragment key={get(group, "[0]", index)}>
                  <Divider textAlign="center">
                    <Typography variant="body2" color="lightgrey">
                      {
                        t("blocks:trainings_the", {
                          date: get(group, "[0]", ""),
                          interpolation: { escapeValue: false },
                        })
                      }
                    </Typography>
                  </Divider>
                  {group[1].map((exam) => (
                    <ListItem
                      disableGutters
                      key={exam.id}
                      className="listItemExam"
                    >
                      <ListItemButton
                        component={Link}
                        disableRipple
                        to={routes.DASHBOARD_ROUTES.EXAM_VIEW.replace(
                          ":id",
                          String(exam.id),
                        )}
                        sx={{ width: "100%" }}
                      >
                        <Grid container spacing={2}>
                          <Grid item md={3} xs={12}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                sx={{ width: 27, height: 27 }}
                                src={exam.horse_trainer?.horse?.avatar_url}
                              />
                              <Typography
                                variant="subtitle2"
                                fontWeight={400}
                                fontSize={{ md: 17, xs: 14 }}
                              >
                                {exam.horse_trainer?.horse?.name
                                  ? upperCase(exam.horse_trainer?.horse?.name)
                                  : t("training.noHorse")}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item md={9} xs={12}>
                            {renderExamInfo(exam)}
                          </Grid>
                        </Grid>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>

            <Box display="flex" justifyContent="center" my={2}>
              <LoadingButton
                disabled={allLoaded}
                onClick={loadMore}
                loading={isLoading}
              >
                {t("loadMore")}
              </LoadingButton>
            </Box>
          </CardContent>

          {/* Backdrop covers the scrollable content */}
          <Backdrop
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              color: "#fff",
              zIndex: 10,
            }}
            open={isLoading}
          ></Backdrop>
        </Box>
      </Card>
    </Stack>
  );
}

export default memo(ListExam);
